import { useState, useCallback, useEffect, useMemo } from "react";
import { Text, Button, Flex, Box } from "@radix-ui/themes";
import { Dialog } from "@/components/shared/dialog";
import Select from "@/components/shared/select";
import { PlusIcon, Cross2Icon } from "@radix-ui/react-icons";
import { useSimulatorApi } from "@/v2/entities/simulator/Simulator.api";
import { useQuery } from "react-query";
import { ReleaseResourceAccessStatus, ReleaseStatus } from "@/routes/simulator/types";
import { SimulatorChannelIcon } from "@/routes/simulators/SimulatorChannelIcon";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import type { ReleaseAccessSnippet } from "@/v2/entities/KnowledgeSnippet/KnowledgeSnippet.model";
import styles from "./VerifySnippetDialog.module.scss";

type AgentAccessType = "ALL_AGENTS" | "SELECTED_AGENTS" | "NO_ACCESS";

interface VerifySnippetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultVersionName?: string;
  hasUnsavedChanges: boolean;
  releaseAccessSnippets: ReleaseAccessSnippet[];
  onVerify: (
    versionName: string,
    approvalNote?: string,
    releaseAccessSnippets?: ReleaseAccessSnippet[]
  ) => Promise<void> | void;
}

export function VerifySnippetDialog({
  open,
  onOpenChange,
  defaultVersionName,
  hasUnsavedChanges,
  releaseAccessSnippets,
  onVerify,
}: VerifySnippetDialogProps) {
  const [versionName, setVersionName] = useState(defaultVersionName || "");
  const [approvalNote, setApprovalNote] = useState("");
  const [showApprovalNote, setShowApprovalNote] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [agentAccessType, setAgentAccessType] = useState<AgentAccessType>("ALL_AGENTS");
  const [selectedSimulatorIds, setSelectedSimulatorIds] = useState<number[]>([]);
  const [isAddAgentsDialogOpen, setIsAddAgentsDialogOpen] = useState(false);
  
  const simulatorApi = useSimulatorApi();
  const { data: simulators, isLoading: isLoadingSimulators } = useQuery({
    queryKey: ["simulators"],
    queryFn: () => simulatorApi.getSimulators(),
    enabled: open,
  });

  // Determine the initial agent access type based on the release access snippets
  // If all releases have full access, set the agent access type to "ALL_AGENTS"
  // If all releases have no access, set the agent access type to "NO_ACCESS"
  // If some releases have full access and some have no access, set the agent access type to "SELECTED_AGENTS"
  // If there are no releases, set the agent access type to "ALL_AGENTS"
  const initialAgentAccessType = useMemo(() => {
    if (!simulators || simulators.length === 0) {
      return "ALL_AGENTS";
    }

    if (!releaseAccessSnippets || releaseAccessSnippets.length === 0) {
      return "ALL_AGENTS";
    }

    const releaseAccessMap = new Map<string, ReleaseResourceAccessStatus>();
    releaseAccessSnippets.forEach((ras) => {
      releaseAccessMap.set(ras.releaseId, ras.status);
    });

    let allReleasesHaveNoAccess = true;
    let allReleasesHaveFullAccess = true;
    let hasAnyRelease = false;

    simulators.forEach((sim) => {
      if (sim.publishedReleaseId != null) {
        hasAnyRelease = true;
        const status = releaseAccessMap.get(sim.publishedReleaseId.toString());
        if (status === ReleaseResourceAccessStatus.FULL_ACCESS) {
          allReleasesHaveNoAccess = false;
        } else if (status === ReleaseResourceAccessStatus.NO_ACCESS) {
          allReleasesHaveFullAccess = false;
        } else {
          allReleasesHaveNoAccess = false;
          allReleasesHaveFullAccess = false;
        }
      }

      const draftRelease = sim.releases?.find(
        (r) => r.status === ReleaseStatus.draft && !r.publishedAt
      );
      if (draftRelease) {
        hasAnyRelease = true;
        const status = releaseAccessMap.get(draftRelease.id.toString());
        if (status === ReleaseResourceAccessStatus.FULL_ACCESS) {
          allReleasesHaveNoAccess = false;
        } else if (status === ReleaseResourceAccessStatus.NO_ACCESS) {
          allReleasesHaveFullAccess = false;
        } else {
          allReleasesHaveNoAccess = false;
          allReleasesHaveFullAccess = false;
        }
      }
    });

    if (!hasAnyRelease) {
      return "ALL_AGENTS";
    }

    if (allReleasesHaveNoAccess) {
      return "NO_ACCESS";
    }

    if (allReleasesHaveFullAccess) {
      return "ALL_AGENTS";
    }

    return "SELECTED_AGENTS";
  }, [releaseAccessSnippets, simulators]);

  // Determine the initial selected simulators based on the agent access type and release access snippets
  // If the agent access type is "SELECTED_AGENTS", select the simulators that their releases have full or limited access
  const initialSelectedSimulators = useMemo(() => {
    if (initialAgentAccessType !== "SELECTED_AGENTS" || !simulators) {
      return [];
    }

    const releaseAccessMap = new Map<string, ReleaseResourceAccessStatus>();
    releaseAccessSnippets.forEach((ras) => {
      releaseAccessMap.set(ras.releaseId, ras.status);
    });

    const selectedSims: number[] = [];
    
    simulators.forEach((sim) => {
      const releaseIds: string[] = [];
      
      if (sim.publishedReleaseId != null) {
        releaseIds.push(sim.publishedReleaseId.toString());
      }
      
      const draftRelease = sim.releases?.find(
        (r) => r.status === ReleaseStatus.draft && !r.publishedAt
      );
      if (draftRelease) {
        releaseIds.push(draftRelease.id.toString());
      }

      if (releaseIds.length > 0) {
        const allHaveAccess = releaseIds.every(
          (releaseId) => releaseAccessMap.get(releaseId) !== ReleaseResourceAccessStatus.NO_ACCESS
        );
        
        if (allHaveAccess) {
          selectedSims.push(sim.id);
        }
      }
    });

    return selectedSims;
  }, [initialAgentAccessType, releaseAccessSnippets, simulators]);

  // Track simulators that have LIMITED_ACCESS on their published release
  const limitedAccessSimulatorIds = useMemo(() => {
    if (!simulators) return new Set<number>();

    const releaseAccessMap = new Map<string, ReleaseResourceAccessStatus>();
    releaseAccessSnippets.forEach((ras) => {
      releaseAccessMap.set(ras.releaseId, ras.status);
    });

    const limitedSims = new Set<number>();

    simulators.forEach((sim) => {
      // Only check published release for LIMITED_ACCESS status
      if (sim.publishedReleaseId != null) {
        const status = releaseAccessMap.get(sim.publishedReleaseId.toString());
        if (status === ReleaseResourceAccessStatus.LIMITED_ACCESS) {
          limitedSims.add(sim.id);
        }
      }
    });

    return limitedSims;
  }, [releaseAccessSnippets, simulators]);

  useEffect(() => {
    if (open) {
      setVersionName(defaultVersionName || "");
      setApprovalNote("");
      setShowApprovalNote(false);
      setAgentAccessType(initialAgentAccessType);
      setSelectedSimulatorIds(initialSelectedSimulators);
      setIsAddAgentsDialogOpen(false);
    }
  }, [open, defaultVersionName, initialAgentAccessType, initialSelectedSimulators]);

  // Clear selected agents when access type changes to "ALL_AGENTS" or "NO_ACCESS"
  useEffect(() => {
    if (agentAccessType === "ALL_AGENTS" || agentAccessType === "NO_ACCESS") {
      setSelectedSimulatorIds([]);
    }
  }, [agentAccessType]);

  // Build the release access snippets based on the agent access type and selected simulators
  // If the feature flag is disabled, always return FULL_ACCESS for all releases
  // If the agent access type is "ALL_AGENTS", set all releases to full access
  // If the agent access type is "NO_ACCESS", set all releases to no access
  // If the agent access type is "SELECTED_AGENTS":
  //   - If the simulator originally had LIMITED_ACCESS, preserve it
  //   - Otherwise, set to FULL_ACCESS if selected, NO_ACCESS if not
  const buildReleaseAccessSnippets = useCallback((): ReleaseAccessSnippet[] => {
    const releaseAccessSnippets: ReleaseAccessSnippet[] = [];

    if (!simulators) {
      return releaseAccessSnippets;
    }

    simulators.forEach((sim) => {
      const isSelected = selectedSimulatorIds.includes(sim.id);
      const hasLimitedAccess = limitedAccessSimulatorIds.has(sim.id);
      
      let accessStatus: ReleaseResourceAccessStatus;
      if (agentAccessType === "ALL_AGENTS") {
        // Preserve LIMITED_ACCESS for simulators that originally had it
        accessStatus = hasLimitedAccess
          ? ReleaseResourceAccessStatus.LIMITED_ACCESS
          : ReleaseResourceAccessStatus.FULL_ACCESS;
      } else if (agentAccessType === "NO_ACCESS") {
        // All releases get NO_ACCESS, including previously LIMITED_ACCESS
        accessStatus = ReleaseResourceAccessStatus.NO_ACCESS;
      } else {
        // SELECTED_AGENTS mode
        if (isSelected) {
          // Preserve LIMITED_ACCESS for simulators that originally had it
          accessStatus = hasLimitedAccess
            ? ReleaseResourceAccessStatus.LIMITED_ACCESS
            : ReleaseResourceAccessStatus.FULL_ACCESS;
        } else {
          accessStatus = ReleaseResourceAccessStatus.NO_ACCESS;
        }
      }

      if (sim.publishedReleaseId != null) {
        releaseAccessSnippets.push({
          releaseId: sim.publishedReleaseId.toString(),
          status: accessStatus,
        });
      }

      const draftRelease = sim.releases?.find(
        (r) => r.status === ReleaseStatus.draft && !r.publishedAt
      );
      if (draftRelease) {
        releaseAccessSnippets.push({
          releaseId: draftRelease.id.toString(),
          status: accessStatus,
        });
      }
    });

    return releaseAccessSnippets;
  }, [agentAccessType, simulators, selectedSimulatorIds, limitedAccessSimulatorIds]);

  const handleMarkAsVerified = useCallback(async () => {
    if (isSubmitting || !versionName.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const note = showApprovalNote ? approvalNote : undefined;
      const releaseAccessSnippets = buildReleaseAccessSnippets();      
      await onVerify(versionName, note, releaseAccessSnippets);
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    versionName,
    showApprovalNote,
    approvalNote,
    onVerify,
    onOpenChange,
    buildReleaseAccessSnippets,
  ]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleAddApprovalNote = useCallback(() => {
    setShowApprovalNote(true);
  }, []);

  const handleAgentAccessTypeChange = useCallback((value: string) => {
    const newType = value as AgentAccessType;
    setAgentAccessType(newType);
    
    // Clear selected agents when switching to "ALL_AGENTS" or "NO_ACCESS"
    if (newType === "ALL_AGENTS" || newType === "NO_ACCESS") {
      setSelectedSimulatorIds([]);
    } else if (newType === "SELECTED_AGENTS" && selectedSimulatorIds.length === 0) {
      // If switching to "SELECTED_AGENTS" and no agents are selected, restore initial selection
      setSelectedSimulatorIds(initialSelectedSimulators);
    }
  }, [initialSelectedSimulators, selectedSimulatorIds.length]);

  const handleAddAgents = useCallback(() => {
    setIsAddAgentsDialogOpen(true);
  }, []);

  const handleRemoveSimulator = useCallback((simulatorId: number) => {
    setSelectedSimulatorIds((prev) => prev.filter((id) => id !== simulatorId));
  }, []);

  const handleToggleSimulator = useCallback((simulatorId: number) => {
    setSelectedSimulatorIds((prev) => {
      if (prev.includes(simulatorId)) {
        return prev.filter((id) => id !== simulatorId);
      }
      return [...prev, simulatorId];
    });
  }, []);

  const selectedSimulators = useMemo(() => {
    return simulators?.filter((sim) => selectedSimulatorIds.includes(sim.id)) ?? [];
  }, [simulators, selectedSimulatorIds]);

  const agentAccessOptions = [
    { label: "All AI Agents", value: "ALL_AGENTS" },
    { label: "Selected Agents", value: "SELECTED_AGENTS" },
    { label: "No Access", value: "NO_ACCESS" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="large" className={styles.dialogContent}>
        <Dialog.Close className={styles.closeButton} />
        <div className={styles.contentWrapper}>
          <Flex direction="column" gap="6" p="5">
            <Flex direction="column" gap="1">
              <Text size="4" weight="medium" className={styles.title}>
                Verify Snippet
              </Text>
              <Text size="3" color="gray" className={styles.subtitle}>
                Verify and publish this snippet to make it the trusted version used by AI
                agents.
                {hasUnsavedChanges && (
                  <Text as="span" size="3" color="orange" weight="medium">
                    {" "}
                    (This will save your content changes)
                  </Text>
                )}
              </Text>
            </Flex>

            <Flex direction="column" gap="2">
              <label className={styles.label}>
                <Text size="2">Version name</Text>
              </label>
              <input
                type="text"
                className={styles.input}
                value={versionName}
                onChange={(e) => setVersionName(e.target.value)}
                placeholder="Verified Version 04"
              />
              <Button
                variant="ghost"
                size="1"
                className={styles.addNoteButton}
                onClick={handleAddApprovalNote}
              >
                <PlusIcon width={12} height={12} />
                <Text size="2">Add Approval note</Text>
              </Button>

              {showApprovalNote && (
                <textarea
                  className={styles.textarea}
                  value={approvalNote}
                  onChange={(e) => setApprovalNote(e.target.value)}
                  placeholder="Add an optional approval note..."
                  rows={3}
                />
              )}
            </Flex>

            <Flex direction="column" gap="2">
              <label className={styles.label}>
                <Text size="2">Select agent access</Text>
              </label>

              <Select
                options={agentAccessOptions}
                value={agentAccessType}
                onChange={handleAgentAccessTypeChange}
                size="medium"
              />
              
              {agentAccessType === "SELECTED_AGENTS" && (
                <Flex direction="column" gap="2" mt="2">
                  {selectedSimulators.length > 0 ? (
                    <Flex wrap="wrap" gap="2">
                      {selectedSimulators.map((sim) => (
                        <Flex
                          key={sim.id}
                          align="center"
                          gap="2"
                          className={styles.agentTag}
                        >
                          <SimulatorChannelIcon channel={sim.channel} size="1" />
                          <Text size="2">{sim.name}</Text>
                          <Button
                            variant="ghost"
                            size="1"
                            onClick={() => handleRemoveSimulator(sim.id)}
                            className={styles.removeButton}
                          >
                            <Cross2Icon width={12} height={12} />
                          </Button>
                        </Flex>
                      ))}
                    </Flex>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="1"
                    className={styles.addAgentsButton}
                    onClick={handleAddAgents}
                  >
                    <PlusIcon width={12} height={12} />
                    <Text size="2">Add Agents</Text>
                  </Button>
                </Flex>
              )}
            </Flex>
          </Flex>
        </div>
        <Box p="2" px="3" mx="5" mb="3" className={styles.infoMessage}>
          <Text size="2" weight="medium" className={styles.infoText}>
            Once verified, this version becomes the trusted source for all selected AI agents and will be used in live responses.
          </Text>
        </Box>
        <Flex gap="3" justify="end" p="5" className={styles.buttonContainer}>
          <Button
            variant="ghost"
            size="2"
            onClick={handleCancel}
            className={styles.cancelButton}
          >
            <Text size="3" color="gray">
              Cancel
            </Text>
          </Button>
          <Button
            variant="solid"
            disabled={!versionName.trim() || isSubmitting}
            color="gray"
            size="2"
            highContrast
            onClick={handleMarkAsVerified}
            className={styles.verifyButton}
          >
            {isSubmitting ? "Saving..." : "Verify Snippet"}
          </Button>
        </Flex>
      </Dialog.Content>

      {/* Add Agents Dialog */}
      <Dialog open={isAddAgentsDialogOpen} onOpenChange={setIsAddAgentsDialogOpen}>
        <Dialog.Content size="small" className={styles.dialogContent}>
          <Dialog.Close className={styles.closeButton} />
          <Flex direction="column" gap="1" p="5" pb="0">
            <Text size="4" weight="medium" className={styles.title}>
              Add Agents
            </Text>
            <Text size="3" color="gray" className={styles.subtitle}>
              Select AI agents that should have access to this snippet
            </Text>
          </Flex>
          <div className={styles.contentWrapper}>
            <Flex direction="column" gap="2" p="5" pt="4">
              {isLoadingSimulators ? (
                <Flex align="center" justify="center" p="6">
                  <Text size="2" color="gray">Loading agents...</Text>
                </Flex>
              ) : (
                <>
                  {simulators?.sort((a, b) => a.name.localeCompare(b.name)).map((sim) => {
                    const isSelected = selectedSimulatorIds.includes(sim.id);
                    return (
                      <Flex
                        key={sim.id}
                        align="center"
                        gap="2"
                        className={styles.agentOption}
                        onClick={() => handleToggleSimulator(sim.id)}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          className={styles.checkbox}
                        />
                        <SimulatorChannelIcon channel={sim.channel} size="1" />
                        <Text size="2">{sim.name}</Text>
                      </Flex>
                    );
                  })}
                  {simulators?.length === 0 && (
                    <Text size="2" color="gray" align="center">
                      No AI agents available
                    </Text>
                  )}
                </>
              )}
            </Flex>
          </div>
          <Flex gap="3" justify="end" p="5" className={styles.buttonContainer}>
            <Button
              variant="ghost"
              size="2"
              onClick={() => setIsAddAgentsDialogOpen(false)}
              className={styles.cancelButton}
            >
              <Text size="3" color="gray">
                Done
              </Text>
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog>
    </Dialog>
  );
}
